/**
 * 流水页 **筛选草稿 → 请求**（纯逻辑层 · C 域 PRD **§5.1.4 的 10 个维度**）
 *
 * 依据：C 域 PRD **§5.1.3**（「加载更多」· 50 笔/批）· **§5.1.4**（10 维 · 维度间 AND / 维度内 OR ·
 * 默认只开「近 3 月」）· **§5.3**（搜索框 = 维度 10 · 默认全时间）· `AC-C1.8` / `AC-C1.13` /
 * `AC-C1.17` / `AC-C1.32` · `ListConfig`（页大小与默认窗口的**唯一常量源**）。
 *
 * ## 🔴 为什么"草稿 → 请求"必须抽出来（本文件存在的理由）
 * 与 `txn-draft.ts` / `account-draft.ts` 同一范式：
 * 页面只该管"长什么样"，"点了这些之后请求长什么样"是**纯函数** ⇒
 * ① 它的断言**不需要渲染**（零依赖基座直接跑）；
 * ② 证伪探针能**精确指向一条通路**，而不是"整页红了"。
 *
 * ## 🔴 维度语义（服务端实现，端侧只转发 —— 铁律 24）
 *
 * | # | 维度 | 参数 | 端侧做什么 |
 * |:--:|---|---|---|
 * | 1 | 时间 | `from` / `to` | 只把**窗口边界**算出来（用共享日历内核，见下） |
 * | 2 | 账户 | `accountIds` | 多选 → csv |
 * | 3 | 成员 | `memberIds` | 多选 → csv（🔴 = **记录人**，不是归属人） |
 * | 4 | 分类 | `categoryIds` | 只传**用户点了哪些**；「选 L1 自动含 L2」在**服务端**展开（`AC-C1.14`） |
 * | 5 | 标签 | `tagIds` | 多选 → csv |
 * | 6 | 金额区间 | `amountMin` / `amountMax` | 🔴 元文本 → **整数分**（走内核 `yuanTextToCents`） |
 * | 7 | 方向 | `directions` | 单选（服务端按**腿级可索引**表达判定，端侧不做形态判定） |
 * | 8 | 状态 | `statuses` | 多选 → csv（⚠️ `deleted` 是**可见性开关**，语义在契约里） |
 * | 9 | 来源 | `sources` | 多选 → csv |
 * | 10 | 关键词 | `keyword` | 单框搜索；🔴 **不传时间窗**（服务端检测到它会放宽到全时间） |
 *
 * ## 🔴 时间维度：为什么由端侧算 `from` / `to`，却不算"第二个口径"
 * 「本月 / 上月 / 今年至今」的边界由**共享日历内核**（`monthStart` / `addDays` / `yearStart`）
 * 算出 —— 与服务端窗口解析（`resolvePageWindow`）**用的是同一批函数**，且都用
 * **账本时区**下的今天（`localDayOf(now, ledger.timezone)`，架构 §4.5）。
 * ⇒ 它**不是**手写日期算术的第二份实现，而是同一内核在不同侧的调用（`PageQuerySchema`
 * 本来就把 `from` / `to` 设计成由调用方传入的窗口参数）。
 * ⚠️ **「近 3 月」（默认档）刻意不传 `from`/`to`** —— 让**服务端**用它自己的默认值，
 * 免得"默认窗口是几个月"这个口径在两侧各写一遍。
 */

import {
  ExportFormat,
  ListConfig,
  TxnDirectionFilter,
  TxnSourceFilter,
  TxnStatusFilter,
  TxnWindowPolicy,
  addDays,
  monthStart,
  recentMonthsStart,
  yearStart,
  type TxnWindowValue,
} from '@jiaying/shared';
import { yuanTextToCentsOrNull } from './txn-draft.js';

/**
 * 维度 1「时间」的取值。
 *
 * C 域 PRD §5.1.4 原文五项（近 3 月（默认）/ 本月 / 上月 / 今年至今 / 自定义区间）+
 * 🆕 N 域批 `59` **补两个滚动档**（近 1 月 / 近 6 月）——
 * 因为「流水默认时间窗」偏好（`AC-N2.18`：近 1 / 3 / 6 月）必须能**一一映射**到页内档位，
 * 否则 `'1m'` 会被硬塞进「本月」（那是**日历月**，不是**滚动月** ⇒ 用户会看到意外的范围）。
 */
export const JournalWindow = {
  Recent1m: 'recent1m',
  Recent3m: 'recent3m',
  Recent6m: 'recent6m',
  ThisMonth: 'thisMonth',
  LastMonth: 'lastMonth',
  ThisYear: 'thisYear',
  Custom: 'custom',
} as const;
export type JournalWindow = (typeof JournalWindow)[keyof typeof JournalWindow];

/**
 * 🔴 **偏好（`'1m'|'3m'|'6m'`）→ 页内档位的唯一映射**（N 域 §5.2 ⑤ ↔ C 域筛选器）。
 *
 * 三档**各有其位**（不靠"近似"）：`1m → recent1m` · `3m → recent3m` · `6m → recent6m`。
 * 穷举 + `never` 兜底：将来加第四档而忘了在这里分派 ⇒ **构建期报错**（不是静默映射到默认）。
 */
export function windowForPreference(pref: TxnWindowValue): JournalWindow {
  switch (pref) {
    case '1m':
      return JournalWindow.Recent1m;
    case '3m':
      return JournalWindow.Recent3m;
    case '6m':
      return JournalWindow.Recent6m;
    default: {
      const never: never = pref;
      throw new RangeError(`未处理的流水默认时间窗偏好：${String(never)}`);
    }
  }
}

/** 筛选草稿（= 左侧筛选轨的全部状态；**受控**，页面只持有它） */
export interface JournalFilterDraft {
  /** 维度 1 */
  window: JournalWindow;
  /** 仅 `window === 'custom'` 时使用（`YYYY-MM-DD`；空串 = 该侧不设） */
  from: string;
  to: string;
  /** 维度 2 */
  accountIds: readonly string[];
  /** 维度 3（🔴 **记录人**，不是归属人） */
  memberIds: readonly string[];
  /** 维度 4（只放用户点的；L1 展开在服务端） */
  categoryIds: readonly string[];
  /** 维度 5 */
  tagIds: readonly string[];
  /** 维度 6 —— **用户输入的元文本**（`""` = 不设）；非法文本见 `amountInputError()` */
  amountMinText: string;
  amountMaxText: string;
  /** 维度 7 —— 单选；`''` = 不限 */
  direction: TxnDirectionFilter | '';
  /** 维度 8 */
  statuses: readonly TxnStatusFilter[];
  /** 维度 9 */
  sources: readonly TxnSourceFilter[];
  /** 维度 10 */
  keyword: string;
}

/** 默认态：**只开「近 3 月」**，其余十个维度全空（`AC-C1.17`） */
export const EMPTY_JOURNAL_FILTER: JournalFilterDraft = {
  window: JournalWindow.Recent3m,
  from: '',
  to: '',
  accountIds: [],
  memberIds: [],
  categoryIds: [],
  tagIds: [],
  amountMinText: '',
  amountMaxText: '',
  direction: '',
  statuses: [],
  sources: [],
  keyword: '',
};

/** 非空才进 query（维度内 OR 用**逗号分隔** —— 服务端的 `csvIds()` / `csvEnums()` 就吃这个形态） */
function csv(values: readonly string[]): string | undefined {
  return values.length === 0 ? undefined : values.join(',');
}

/**
 * 从**入口参数**（URL `?statuses=`）初始化筛选草稿。
 *
 * ## 为什么需要它（`AC-G1.14`）
 *
 * 「含待缴 ¥X 未付」标注点进去要**落到按「待缴·未付」筛选好的流水页**。
 * 预算页与流水页是两个路由 ⇒ 这个"初始筛选"必须能**跨路由传递**，URL query 是最直白的载体。
 *
 * 🔴 **只接受合法枚举值**（非法值静默丢弃 —— 不把 URL 当可信输入，也不因一个坏参数整页崩）。
 * 🔴 传空 **且** 用默认窗口 ⇒ **返回 `EMPTY_JOURNAL_FILTER` 本体**（不是副本）——
 *    保持"默认态只有一个实例"，便于断言"没传参时就是默认态"。
 *
 * @param window 🆕 基线窗口（来自用户偏好 `txnWindow` · `AC-N2.18`）—— 缺省 = 近 3 月。
 *   ⚠️ 它**只影响"基线"**，不影响 URL 传入的维度值（两者正交）。
 */
export function initialJournalFilter(
  statuses: readonly string[],
  window: JournalWindow = JournalWindow.Recent3m,
): JournalFilterDraft {
  const allowed = Object.values(TxnStatusFilter) as readonly string[];
  const valid = statuses.filter((s) => allowed.includes(s)) as TxnStatusFilter[];
  if (valid.length === 0 && window === JournalWindow.Recent3m) return EMPTY_JOURNAL_FILTER;
  return { ...EMPTY_JOURNAL_FILTER, window, statuses: valid };
}

/**
 * 维度 1 的窗口边界 → `{ from?, to? }`（缺省 = **交给服务端默认**）。
 *
 * | 档 | from | to |
 * |---|---|---|
 * | 近 3 月（默认基线） | *不传* | *不传*（= 服务端默认窗口，见下） |
 * | 🆕 近 1 月 | `recentMonthsStart(today, 1)` | *不传* |
 * | 🆕 近 6 月 | `recentMonthsStart(today, 6)` | *不传* |
 * | 本月 | `monthStart(today)` | *不传*（服务端 `to` 默认 = 今天） |
 * | 上月 | 上月的 1 号 | **上月的最后一天**（= 本月 1 号 − 1 天） |
 * | 今年至今 | `yearStart(today)` | *不传* |
 * | 自定义 | 用户填的 | 用户填的（空则视为不设） |
 *
 * 🔴 「上月」的 `to` **必须显式给**：只给 `from` 会让 `to` 落到"今天"，
 * 于是"上月"会**一直算到今天**（一个看起来正常、但多算了一个月的窗口）。
 *
 * 🔴 「近 3 月」**刻意不传 `from`**：服务端默认窗口就是 3 个月（`ListConfig.DefaultWindowMonths`），
 * 传了等于把同一个 3 写在两处。🆕 而 1 / 6 月**必须显式传**（服务端不认识它们）。
 *
 * ⚠️ **「近 N 个月」= 近 N 个\*\*自然月\*\*（含本月）**（`recentMonthsStart` 的既有口径 · 架构 §6.4）——
 * 故「近 1 个月」与「本月」是**同一个窗口**（都是从本月 1 号起）。这是 C 域 PRD §5.1.4 的
 * 「本月」档与 N 域 §5.2 ⑤ 的「1/3/6 月」取值集**固有的重叠**（两份 PRD 各自要求），
 * 本批**按既有口径实现并登记**，不擅自改其中任何一份的口径（改"自然月 → 滚动日"须走 DR）。
 */
export function windowRange(
  window: JournalWindow,
  from: string,
  to: string,
  today: string,
): { from?: string; to?: string } {
  switch (window) {
    case JournalWindow.Recent1m:
      // 🆕 滚动**自然月**口径：与「近 3 月」同一条实现（`recentMonthsStart`），
      //    只是月数不同 —— 服务端的默认窗口是 3 个月，故 1 / 6 月**必须显式给 from**。
      return { from: recentMonthsStart(today, 1) };
    case JournalWindow.Recent3m:
      // 🔴 **不传** = 交给服务端默认（`ListConfig.DefaultWindowMonths = 3`）——
      //    故"近 3 月"与"服务端默认窗口"**恒等**，不需要两处各写一个 3。
      return {};
    case JournalWindow.Recent6m:
      return { from: recentMonthsStart(today, 6) };
    case JournalWindow.ThisMonth:
      return { from: monthStart(today) };
    case JournalWindow.LastMonth: {
      const thisMonthStart = monthStart(today);
      return { from: monthStart(addDays(thisMonthStart, -1)), to: addDays(thisMonthStart, -1) };
    }
    case JournalWindow.ThisYear:
      return { from: yearStart(today) };
    case JournalWindow.Custom:
      return { ...(from === '' ? {} : { from }), ...(to === '' ? {} : { to }) };
    default: {
      // 穷举兜底：新增档位却忘了在这里分派 ⇒ **构建期就该报错**（不是静默回落）
      const never: never = window;
      throw new RangeError(`未处理的流水时间档：${String(never)}`);
    }
  }
}

/**
 * 金额输入的提示文案（`null` = 合法或不设）。🔴 供 UI **显式提示**，不静默丢弃。
 *
 * ⚠️ 与 `buildJournalQuery` 的分工：本函数只回答"这串文本能不能用"；
 * 拼请求时**非法值不参与**（且此时 UI 一定已经显示了这条提示）。
 */
export function amountInputError(draft: JournalFilterDraft): string | null {
  for (const [label, text] of [
    ['最小', draft.amountMinText],
    ['最大', draft.amountMaxText],
  ] as const) {
    const trimmed = text.trim();
    if (trimmed === '') continue;
    if (yuanTextToCentsOrNull(trimmed) === null) return `金额${label}值需为数字，最多两位小数`;
  }
  return null;
}

/**
 * 拼流水列表的查询串（**含前导 `?`**）。
 *
 * 参数名逐字对照 `TransactionQuerySchema` + `PageQuerySchema` ——
 * 🔴 这里**不允许**出现契约里没有的键（服务端 `class-validator`/zod 会 400，
 * 而"多传一个键"这种错**只有在真请求时**才暴露 ⇒ 拼装必须是可直测的纯函数，本函数即为此）。
 */
export function buildJournalQuery(
  draft: JournalFilterDraft,
  opts: { today: string; page?: number; pageSize?: number },
): string {
  const parts: [string, string][] = [];
  // 🔴 省略 `page` ⇒ **不传分页参数**，由服务端用默认值（页 1 / 每批 50）。
  //    这样"默认页大小是多少"仍然只有服务端一处口径；调用方要翻页时才显式给 page。
  if (opts.page !== undefined) {
    parts.push(['page', String(opts.page)]);
    parts.push(['pageSize', String(opts.pageSize ?? ListConfig.PageSize)]);
  }

  const win = windowRange(draft.window, draft.from.trim(), draft.to.trim(), opts.today);
  if (win.from !== undefined) parts.push(['from', win.from]);
  if (win.to !== undefined) parts.push(['to', win.to]);

  const lists = [
    ['accountIds', csv(draft.accountIds)],
    ['memberIds', csv(draft.memberIds)],
    ['categoryIds', csv(draft.categoryIds)],
    ['tagIds', csv(draft.tagIds)],
    ['statuses', csv(draft.statuses)],
    ['sources', csv(draft.sources)],
  ] as const;
  for (const [key, value] of lists) if (value !== undefined) parts.push([key, value]);

  // 维度 6：元文本 → **整数分**（🔴 走内核，不在这里做 `/ 100` 或 `Math.round`）
  for (const [key, text] of [
    ['amountMin', draft.amountMinText],
    ['amountMax', draft.amountMaxText],
  ] as const) {
    const trimmed = text.trim();
    if (trimmed === '') continue;
    const cents = yuanTextToCentsOrNull(trimmed);
    if (cents === null) continue; // 非法值不进 query（UI 由 `amountInputError` 提示）
    parts.push([key, String(cents)]);
  }

  // 维度 7：单选 —— 契约收的是 `csvEnums`，单值即单元素 csv
  if (draft.direction !== '') parts.push(['directions', draft.direction]);

  // 维度 10：🔴 只传关键词本身 —— **不传时间窗**（服务端检测到它会放宽到全时间，`AC-C3.5`）
  const keyword = draft.keyword.trim();
  if (keyword !== '') parts.push(['keyword', keyword]);

  return `?${parts.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&')}`;
}

/**
 * 导出**当前筛选**的请求路径（含 `format`）—— `AC-N4.4` 的拼装唯一实现。
 *
 * ## 🔴 为什么它必须和列表共用 `buildJournalQuery`
 *
 * `AC-N4.4` 的原文是「CSV **支持导出当前筛选结果**（修 V1-018）」。V1-018 那个缺陷的本质是
 * **屏幕上看到的与导出的不是同一批行**（列表走一套筛选、导出走另一套）。
 * ⇒ 唯一防法：导出路径**由同一个 `buildJournalQuery()` 拼**（本函数只多做一件事 —— 补 `format`）。
 *
 * ⚠️ `buildJournalQuery` 在**无任何参数**时返回 `'?'`（不是空串）⇒ 分隔符要分情形：
 * `'?' + 'format=csv'` = `?format=csv`（不能写成 `?&format=csv`，那会多一个空参数）。
 */
export function exportCsvPath(draft: JournalFilterDraft, today: string): string {
  const q = buildJournalQuery(draft, { today });
  const sep = q === '?' ? '' : '&';
  return `/api/transactions/export${q}${sep}format=${ExportFormat.Csv}`;
}

/**
 * 已生效的维度数（= 与**基线**不同的维度个数）—— 供筛选轨上的"组合筛选"小标记。
 *
 * 🔴 判据只看**与基线的差**，不看"非空"：基线窗口本身是**开着的**
 * （`AC-C1.17`），把它算进去会让新用户一进页面就看到"已筛选 1 项"。
 *
 * @param base 🆕 **基线窗口** = 用户偏好派生的那一档（`windowForPreference(txnWindow)`）。
 *   ⚠️ 不是恒为「近 3 月」：用户把默认窗口设成「近 6 月」后，落在 6 月档**就是**默认态
 *   （否则他一进页面就看到"已筛选 1 项"—— 那才是假信息）。
 */
export function activeDimensionCount(
  draft: JournalFilterDraft,
  base: JournalWindow = JournalWindow.Recent3m,
): number {
  const dims: boolean[] = [
    draft.window !== base,
    draft.accountIds.length > 0,
    draft.memberIds.length > 0,
    draft.categoryIds.length > 0,
    draft.tagIds.length > 0,
    draft.amountMinText.trim() !== '' || draft.amountMaxText.trim() !== '',
    draft.direction !== '',
    draft.statuses.length > 0,
    draft.sources.length > 0,
    draft.keyword.trim() !== '',
  ];
  return dims.filter(Boolean).length;
}

/** 草稿是否已回到默认态（供"清空"按钮的可用性判断）—— `base` 同 `activeDimensionCount` */
export function isDefaultFilter(
  draft: JournalFilterDraft,
  base: JournalWindow = JournalWindow.Recent3m,
): boolean {
  return activeDimensionCount(draft, base) === 0;
}

/** 🆕 当前用户偏好的默认窗口档（`TxnWindowPolicy.Default` 兜底 ⇒ 与"没有偏好行"时的服务端值一致） */
export function baseWindowOf(pref: TxnWindowValue | undefined): JournalWindow {
  return windowForPreference(pref ?? TxnWindowPolicy.Default);
}
